import { HappyBeaming, Send, ShieldX } from "@styled-icons/boxicons-solid";
import Axios, { CancelTokenSource } from "axios";
import { observer } from "mobx-react-lite";
import { Channel } from "revolt.js";
import styled, { css } from "styled-components/macro";
import { ulid } from "ulid";
import { Text } from "preact-i18n";
import { memo } from "preact/compat";
import { useCallback, useEffect, useMemo, useState } from "preact/hooks";

import {
    Button,
    Error as ErrorComponent,
    GifPicker,
    IconButton,
    Picker,
} from "@revoltchat/ui";

import { debounce } from "../../../lib/debounce";
import { defer } from "../../../lib/defer";
import { getDisplayName } from "../../../lib/displayName";
import { internalEmit, internalSubscribe } from "../../../lib/eventEmitter";
import { getRolesInChannel } from "../../../lib/getRolesInChannel";
import { useOutsideClick } from "../../../lib/hooks/use-outside-click";
import { useTranslation } from "../../../lib/i18n";
import { isTouchscreenDevice } from "../../../lib/isTouchscreenDevice";
import { displayMentions, normalizeMentions } from "../../../lib/mentions";
import {
    getRenderer,
    SMOOTH_SCROLL_ON_RECEIVE,
} from "../../../lib/renderer/Singleton";
import TextAreaAutoSize from "../../../lib/TextAreaAutoSize";

import { useApplicationState } from "../../../mobx/State";
import { DraftObject } from "../../../mobx/stores/Draft";
import { Reply } from "../../../mobx/stores/MessageQueue";

import { dayjs } from "../../../context/Locale";

import { emojiDictionary } from "../../../assets/emojis";
import {
    getClientController,
    useClient,
} from "../../../controllers/client/ClientController";
import { takeError } from "../../../controllers/client/jsx/error";
import {
    FileUploader,
    grabFiles,
    uploadFile,
} from "../../../controllers/client/jsx/legacy/FileUploads";
import { modalController } from "../../../controllers/modals/ModalController";
import { RenderEmoji } from "../../markdown/plugins/emoji";
import AutoComplete, { useAutoComplete } from "../AutoComplete";
import { PermissionTooltip } from "../Tooltip";
import FilePreview from "./bars/FilePreview";
import ReplyBar from "./bars/ReplyBar";
import { FormattingToolbar, useTextFormatting } from "./FormattingToolbar";

type Props = {
    channel: Channel;
};

export type UploadState =
    | { type: "none" }
    | { type: "attached"; files: File[] }
    | {
          type: "uploading";
          files: File[];
          percent: number;
          cancel: CancelTokenSource;
      }
    | { type: "sending"; files: File[] }
    | { type: "failed"; files: File[]; error: string };

const Base = styled.div`
    position: relative;
    z-index: 1;
    display: flex;
    align-items: flex-start;
    background: var(--message-box);

    textarea {
        font-size: var(--text-size);
        background: transparent;

        &::placeholder {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }
`;

const Blocked = styled.div`
    display: flex;
    align-items: center;
    user-select: none;
    font-size: var(--text-size);
    color: var(--tertiary-foreground);
    flex-grow: 1;
    cursor: not-allowed;

    .text {
        padding: var(--message-box-padding);
    }

    > div > div {
        cursor: default;
    }

    svg {
        flex-shrink: 0;
    }
`;

const Action = styled.div`
    > a {
        height: 48px;
        width: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        /*padding: 14px 0 14px 14px;*/
    }

    .mobile {
        width: 62px;
    }

    ${() =>
        !isTouchscreenDevice &&
        css`
            .mobile {
                display: none;
            }
        `}
`;

const ActionsGroup = styled.div`
    display: flex;
`;

const CharsCount = styled.div`
    font-size: 0.75rem;
    font-weight: bold;
    color: var(--secondary-foreground);
    text-align: right;
    width: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: flex-end;
`;

const RightContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const FileAction = styled.div`
    > a {
        height: 48px;
        width: 62px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

const FloatingLayer = styled.div`
    position: relative;
`;

const ThisCodeWillBeReplacedAnywaysSoIMightAsWellJustDoItThisWay__Padding = styled.div`
    width: 16px;
`;

// For sed replacement
const RE_SED = new RegExp("^s/([^])*/([^])*$");

// Tests for code block delimiters (``` at start of line)
const RE_CODE_DELIMITER = new RegExp("^```", "gm");

const EmojiGifSelectorContainer = styled.div`
    position: relative;

    .tabs {
        /* z-index: 1; */
        display: flex;
        gap: 16px;
        padding: 8px;
        position: absolute;
        right: 10px;
        bottom: 58px;
        width: calc(8 * 40px + 10px + 40px);
        height: 420px;
        max-width: calc(100vw - 20px);
        max-height: calc(75vh);
        background: var(--background);
        border-radius: var(--border-radius);

        .active {
            opacity: 0.7;
        }
    }

    .gifs {
        & > div {
            height: 420px;
            max-width: calc(100vw - 20px);
            max-height: calc(75vh);
        }
    }
`;

const EmojiGifSelector = observer(
    ({
        onEmojiSelect,
        onGifSelect,
        onClose,
        canUploadFiles,
    }: {
        onEmojiSelect: (emoji: string) => void;
        onGifSelect: (gif_object: any) => void;
        onClose: () => void;
        canUploadFiles: boolean;
    }) => {
        const [activeTab, setActiveTab] = useState<"emojis" | "gifs">("emojis");
        const containerRef = useOutsideClick(onClose);

        return (
            <EmojiGifSelectorContainer ref={containerRef}>
                <div className="tabs">
                    <Button
                        onClick={() => setActiveTab("emojis")}
                        className={activeTab === "emojis" ? "active" : ""}>
                        Emojis
                    </Button>
                    {canUploadFiles && (
                        <Button
                            onClick={() => setActiveTab("gifs")}
                            className={activeTab === "gifs" ? "active" : ""}>
                            GIFs
                        </Button>
                    )}
                </div>
                {activeTab === "emojis" ? (
                    <EmojiPicker
                        onSelect={(emoji: string) => {
                            onEmojiSelect(emoji);
                            onClose();
                        }}
                        onClose={onClose}
                    />
                ) : (
                    <div class="gifs">
                        <GifPicker
                            onSelect={(gif) => {
                                onGifSelect(gif);
                                onClose();
                            }}
                        />
                    </div>
                )}
            </EmojiGifSelectorContainer>
        );
    },
);

export const EmojiPicker = observer(
    ({
        onSelect,
        onClose,
    }: {
        onSelect: (emoji: string) => void;
        onClose: () => void;
    }) => {
        const translate = useTranslation();
        const appState = useApplicationState();
        const renderEmoji = useMemo(
            () =>
                memo(({ emoji }: { emoji: string }) => (
                    <RenderEmoji match={emoji} {...({} as any)} />
                )),
            [],
        );

        const mostUsed = appState.emojis.mostUsed;
        const chosen = appState.emojis.chosen;

        const onEmojiSelected = (emoji: string) => {
            mostUsed.increaseRank(emoji);
            onSelect(emoji);
        };

        const emojis: Record<string, any> = {
            default: Object.keys(emojiDictionary).map((id) => ({
                id,
                isChosen: chosen.has(id),
            })),
            chosen: chosen.list.map((emoji) => ({ id: emoji, isChosen: true })),
            mostUsed: mostUsed.list.map((emoji) => ({
                id: emoji,
                isChosen: chosen.has(emoji),
            })),
        };

        // ! FIXME: also expose typing from component
        const categories: any[] = [];

        if (chosen.list.length) {
            categories.push({
                id: "chosen",
                name: translate(
                    "app.special.popovers.emoji_picker.emoji_category.chosen",
                ),
                emoji: "heart_decoration",
            });
        }

        if (mostUsed.list.length) {
            categories.push({
                id: "mostUsed",
                name: translate(
                    "app.special.popovers.emoji_picker.emoji_category.most_used",
                ),
                emoji: "sparkles",
            });
        }

        for (const server of appState.ordering.orderedServers) {
            // ! FIXME: add a separate map on each server for emoji
            const list = [...getClientController().readyClient!.emojis.values()]
                .filter(
                    (emoji) =>
                        emoji.parent.type !== "Detached" &&
                        emoji.parent.id === server._id,
                )
                .map(({ _id, name }) => ({ id: _id, name }));

            if (list.length > 0) {
                emojis[server._id] = list;
                categories.push({
                    id: server._id,
                    name: server.name,
                    iconURL: server.generateIconURL({ max_side: 256 }),
                });
            }
        }

        categories.push({
            id: "default",
            name: translate(
                "app.special.popovers.emoji_picker.emoji_category.default",
            ),
            emoji: "smiley",
        });

        return (
            <Picker
                emojis={emojis}
                categories={categories}
                renderEmoji={renderEmoji}
                onSelect={onEmojiSelected}
                onClose={onClose}
                addChosen={(emoji) => {
                    appState.emojis.chosen.add(emoji);
                }}
                deleteChosen={(emoji) => {
                    appState.emojis.chosen.delete(emoji);
                }}
            />
        );
    },
);

// ! FIXME: add to app config and load from app config
export const CAN_UPLOAD_AT_ONCE = 10;

export const MAX_FILE_SIZE = 256_000_000;

const MAX_MESSAGE_LENGTH = 90_000;
const MAX_MESSAGE_LENGTH_OFFSET_BEFORE_WARNING = 5_000;

const MESSAGE_ID = "message";

export default observer(({ channel }: Props) => {
    const state = useApplicationState();

    const [uploadState, setUploadState] = useState<UploadState>({
        type: "none",
    });
    const [typing, setTyping] = useState<boolean | number>(false);
    const [replies, setReplies] = useState<Reply[]>([]);
    const [picker, setPicker] = useState(false);
    const [cursorPoint, setCursorPoint] = useState<number | null>(null);
    const client = useClient();
    const translate = useTranslation();

    const closePicker = useCallback(() => setPicker(false), []);

    const renderer = getRenderer(channel);

    useEffect(() => {
        if (uploadState.type === "attached" || uploadState.type === "sending") {
            internalEmit("TextArea", "focus", MESSAGE_ID);
        }
    }, [uploadState.type]);

    if (channel.server?.member?.timeout) {
        return (
            <Base>
                <Blocked>
                    <Action>
                        <PermissionTooltip
                            permission="SendMessages"
                            placement="top">
                            <ShieldX size={22} />
                        </PermissionTooltip>
                    </Action>
                    <div className="text">
                        <Text
                            id="app.main.channel.misc.timed_out"
                            fields={{
                                // TODO: make this reactive
                                time: dayjs().to(
                                    channel.server.member.timeout,
                                    true,
                                ),
                            }}
                        />
                    </div>
                </Blocked>
            </Base>
        );
    }

    if (!channel.havePermission("SendMessage")) {
        return (
            <Base>
                <Blocked>
                    <Action>
                        <PermissionTooltip
                            permission="SendMessages"
                            placement="top">
                            <ShieldX size={22} />
                        </PermissionTooltip>
                    </Action>
                    <div className="text">
                        <Text id="app.main.channel.misc.no_sending" />
                    </div>
                </Blocked>
            </Base>
        );
    }

    // Push message content to draft.
    const setMessage = useCallback(
        (content?: string) => {
            const dobj: DraftObject = {
                content,
            };
            state.draft.set(channel._id, dobj);
        },
        [state.draft, channel._id],
    );

    useEffect(() => {
        /**
         *
         * @param content
         * @param action
         */
        function append(content: string, action: "quote" | "mention") {
            const text =
                action === "quote"
                    ? `${content
                          .split("\n")
                          .map((x) => `> ${x}`)
                          .join("\n")}\n\n`
                    : `${content} `;

            if (!state.draft.has(channel._id)) {
                setMessage(text);
            } else {
                setMessage(`${state.draft.get(channel._id)?.content}\n${text}`);
            }
        }

        return internalSubscribe(
            "MessageBox",
            "append",
            append as (...args: unknown[]) => void,
        );
    }, [state.draft, channel._id, setMessage]);

    /**
     * Trigger send message.
     */
    async function send() {
        if (uploadState.type === "uploading" || uploadState.type === "sending")
            return;

        if (currentMessageContentLength > MAX_MESSAGE_LENGTH) {
            return;
        }

        const content = replaceNames(
            state.draft.get(channel._id)?.content?.trim() ?? "",
        );

        if (uploadState.type !== "none") return sendFile(content);
        if (content.length === 0) return;

        internalEmit("NewMessages", "hide");
        stopTyping();
        setMessage();
        setReplies([]);
        const nonce = ulid();

        // sed style message editing.
        // If the user types for example `s/abc/def`, the string "abc"
        // will be replaced with "def" in their last sent message.
        if (RE_SED.test(content)) {
            renderer.messages.reverse();
            const msg = renderer.messages.find(
                (msg) => msg.author_id === client.user!._id,
            );
            renderer.messages.reverse();

            if (msg?.content) {
                // eslint-disable-next-line prefer-const
                let [_, toReplace, newText, flags] = content.split(/\//);

                if (toReplace == "*") toReplace = msg.content.toString();

                const newContent =
                    toReplace == ""
                        ? msg.content.toString() + newText
                        : msg.content
                              .toString()
                              .replace(new RegExp(toReplace, flags), newText);

                if (newContent != msg.content) {
                    if (newContent.length == 0) {
                        msg.delete().catch(console.error);
                    } else {
                        msg.edit({
                            content: newContent.substr(0, 2000),
                        })
                            .then(() =>
                                defer(() =>
                                    renderer.jumpToBottom(
                                        SMOOTH_SCROLL_ON_RECEIVE,
                                    ),
                                ),
                            )
                            .catch(console.error);
                    }
                }
            }
        } else {
            state.settings.sounds.playSound("outbound");

            state.queue.add(nonce, channel._id, {
                _id: nonce,
                channel: channel._id,
                author: client.user!._id,

                content,
                replies,
            });

            // Было в defer
            renderer.jumpToBottom(SMOOTH_SCROLL_ON_RECEIVE);

            try {
                await channel.sendMessage({
                    content,
                    nonce,
                    replies,
                });
            } catch (error) {
                state.queue.fail(nonce, takeError(error));
                setTimeout(() => state.queue.remove(nonce), 5 * 1000);
            }
        }
        setCursorPoint(null);
    }

    /**
     *
     * @param content
     * @returns
     */
    async function sendFile(content: string) {
        if (uploadState.type !== "attached" && uploadState.type !== "failed")
            return;

        const attachments: string[] = [];
        const cancel = Axios.CancelToken.source();
        const files = uploadState.files;
        stopTyping();
        setUploadState({ type: "uploading", files, percent: 0, cancel });

        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                attachments.push(
                    await uploadFile(
                        client.configuration!.features.autumn.url,
                        "attachments",
                        file,
                        {
                            onUploadProgress: (e) =>
                                setUploadState({
                                    type: "uploading",
                                    files,
                                    percent: Math.round(
                                        (i * 100 + (100 * e.loaded) / e.total) /
                                            files.length,
                                    ),
                                    cancel,
                                }),
                            cancelToken: cancel.token,
                        },
                    ),
                );
            }
        } catch (err) {
            // eslint-disable-next-line
            if ((err as any)?.message === "cancel") {
                setUploadState({
                    type: "attached",
                    files,
                });
            } else {
                setUploadState({
                    type: "failed",
                    files,
                    error: takeError(err),
                });
            }
            return;
        }

        setUploadState({
            type: "sending",
            files,
        });

        try {
            for (
                let offset = 0;
                offset < attachments.length;
                offset += CAN_UPLOAD_AT_ONCE
            ) {
                const batch = attachments.slice(
                    offset,
                    offset + CAN_UPLOAD_AT_ONCE,
                );
                const nonce = ulid();

                await channel.sendMessage({
                    content: offset === 0 ? content : null,
                    nonce,
                    replies: offset === 0 ? replies : null,
                    attachments: batch,
                });
            }
        } catch (err) {
            setUploadState({
                type: "failed",
                files,
                error: takeError(err),
            });
            return;
        }

        setMessage();
        setReplies([]);
        state.settings.sounds.playSound("outbound");
        setUploadState({ type: "none" });
    }

    async function fetchGifBlob(url: string) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10 * 1000);
        try {
            const resp = await fetch(url, {
                signal: controller.signal,
            });
            if (resp.status !== 200) {
                throw new Error(`gif url returned bad status ${resp.status}`);
            }
            const blob = await resp.blob();
            return blob;
        } finally {
            clearTimeout(timeoutId);
        }
    }

    async function onGifSelect(gif_object: any) {
        if (!["none", "attached"].includes(uploadState.type)) {
            return;
        }
        const url = gif_object.images.downsized_large.url;
        let gifBlob;
        try {
            gifBlob = await fetchGifBlob(url);
        } catch (e) {
            console.error(`Failed to download gif: ${e}`);
            return;
        }
        const file = new File([gifBlob], `${gif_object.title}.gif`, {
            type: "image/gif",
        });

        if (uploadState.type === "none") {
            setUploadState({ type: "attached", files: [file] });
        } else if (uploadState.type === "attached") {
            setUploadState({
                type: "attached",
                files: [...uploadState.files, file],
            });
        }
    }

    /**
     *
     * @returns
     */
    function startTyping() {
        if (typeof typing === "number" && +new Date() < typing) return;

        const ws = client.websocket;
        if (ws.connected) {
            setTyping(+new Date() + 2500);
            ws.send({
                type: "BeginTyping",
                channel: channel._id,
            });
        }
    }

    /**
     *
     * @param force
     */
    function stopTyping(force?: boolean) {
        if (force || typing) {
            const ws = client.websocket;
            if (ws.connected) {
                setTyping(false);
                ws.send({
                    type: "EndTyping",
                    channel: channel._id,
                });
            }
        }
    }

    function isInCodeBlock(cursor: number): boolean {
        const content = state.draft.get(channel._id)?.content || "";
        const contentBeforeCursor = content.substring(0, cursor);

        let delimiterCount = 0;
        for (const delimiter of contentBeforeCursor.matchAll(
            RE_CODE_DELIMITER,
        )) {
            delimiterCount++;
        }

        // Odd number of ``` delimiters before cursor => we are in code block
        return delimiterCount % 2 === 1;
    }

    // TODO: change to useDebounceCallback
    // eslint-disable-next-line
    const debouncedStopTyping = useCallback(
        debounce(stopTyping as (...args: unknown[]) => void, 1000),
        [channel._id],
    );
    const {
        onChange,
        onKeyUp,
        onKeyDown,
        onFocus,
        onBlur,
        ...autoCompleteProps
    } = useAutoComplete(
        setMessage,
        channel.havePermission("MentionRoles"),
        {
            users: { type: "channel", id: channel._id },
            channels:
                channel.channel_type === "TextChannel"
                    ? { server: channel.server_id! }
                    : undefined,
        },
        MESSAGE_ID,
    );

    const content = state.draft.get(channel._id)?.content || "";

    const {
        isShowFormattingToolbar,
        closeFormattingToolbar,
        onSelect,
        applyFormatting,
    } = useTextFormatting(content, setMessage);

    const server_id = channel.server_id;
    function replaceMentions(s: string) {
        return displayMentions(
            s,
            client,
            server_id,
            getRolesInChannel(channel),
        );
    }

    function replaceNames(s: string) {
        return normalizeMentions(
            s,
            client,
            server_id,
            getRolesInChannel(channel),
        );
    }

    const currentMessageContentLength =
        state.draft.get(channel._id)?.content?.length || 0;

    return (
        <>
            <AutoComplete {...autoCompleteProps} />
            <FilePreview
                state={uploadState}
                addFile={() =>
                    uploadState.type === "attached" &&
                    grabFiles(
                        MAX_FILE_SIZE,
                        (files) =>
                            setUploadState({
                                type: "attached",
                                files: [...uploadState.files, ...files],
                            }),
                        (filesize) =>
                            modalController.push({
                                type: "file_too_large",
                                filesize,
                            }),
                        true,
                    )
                }
                removeFile={(index) => {
                    if (uploadState.type !== "attached") return;
                    if (uploadState.files.length === 1) {
                        setUploadState({ type: "none" });
                    } else {
                        setUploadState({
                            type: "attached",
                            files: uploadState.files.filter(
                                (_, i) => index !== i,
                            ),
                        });
                    }
                }}
            />
            <ReplyBar
                channel={channel}
                replies={replies}
                setReplies={setReplies}
            />
            <FloatingLayer>
                {picker && (
                    <EmojiGifSelector
                        onEmojiSelect={(emoji) => {
                            const v = state.draft.get(channel._id);
                            const cnt: DraftObject = {
                                content: "",
                            };
                            if (v?.content) {
                                if (cursorPoint !== null) {
                                    cnt.content = `${
                                        cursorPoint > 0
                                            ? v.content.slice(0, cursorPoint)
                                            : ""
                                    } :${emoji}: ${v.content.slice(
                                        cursorPoint,
                                    )}`;
                                } else {
                                    cnt.content = `${v?.content} :${emoji}:`;
                                }
                            } else {
                                cnt.content = `:${emoji}:`;
                            }
                            state.draft.set(channel._id, cnt);
                        }}
                        onGifSelect={onGifSelect}
                        onClose={closePicker}
                        canUploadFiles={channel.havePermission("UploadFiles")}
                    />
                )}
            </FloatingLayer>
            <Base>
                {channel.havePermission("UploadFiles") ? (
                    <FileAction>
                        <FileUploader
                            size={24}
                            behaviour="multi"
                            style="attachment"
                            fileType="attachments"
                            maxFileSize={MAX_FILE_SIZE}
                            attached={uploadState.type !== "none"}
                            uploading={
                                uploadState.type === "uploading" ||
                                uploadState.type === "sending"
                            }
                            remove={async () =>
                                setUploadState({ type: "none" })
                            }
                            onChange={(files) =>
                                setUploadState({ type: "attached", files })
                            }
                            cancel={() =>
                                uploadState.type === "uploading" &&
                                uploadState.cancel.cancel("cancel")
                            }
                            append={(files) => {
                                if (files.length === 0) return;

                                if (uploadState.type === "none") {
                                    setUploadState({ type: "attached", files });
                                } else if (uploadState.type === "attached") {
                                    setUploadState({
                                        type: "attached",
                                        files: [...uploadState.files, ...files],
                                    });
                                }
                            }}
                        />
                    </FileAction>
                ) : (
                    <ThisCodeWillBeReplacedAnywaysSoIMightAsWellJustDoItThisWay__Padding />
                )}
                {!isTouchscreenDevice && isShowFormattingToolbar && (
                    <FormattingToolbar
                        onFormat={applyFormatting}
                        onClose={closeFormattingToolbar}
                    />
                )}
                <TextAreaAutoSize
                    onSelect={onSelect}
                    autoFocus={!modalController.isVisible}
                    hideBorder
                    maxRows={20}
                    id={MESSAGE_ID}
                    onKeyUp={(e) => {
                        setCursorPoint(e.target.selectionStart);
                        onKeyUp(e);
                    }}
                    value={replaceMentions(
                        state.draft.get(channel._id)?.content || "",
                    )}
                    padding="var(--message-box-padding)"
                    onKeyDown={(e) => {
                        if (e.ctrlKey && e.key === "Enter") {
                            e.preventDefault();
                            return send();
                        }

                        if (onKeyDown(e)) return;

                        if (
                            e.key === "ArrowUp" &&
                            !state.draft.has(channel._id)
                        ) {
                            e.preventDefault();
                            internalEmit("MessageRenderer", "edit_last");
                            return;
                        }

                        if (
                            !e.shiftKey &&
                            !e.isComposing &&
                            e.key === "Enter" &&
                            !isTouchscreenDevice &&
                            !isInCodeBlock(e.currentTarget.selectionStart)
                        ) {
                            e.preventDefault();
                            return send();
                        }

                        if (e.key === "Escape") {
                            if (replies.length > 0) {
                                setReplies(replies.slice(0, -1));
                            } else if (
                                uploadState.type === "attached" &&
                                uploadState.files.length > 0
                            ) {
                                setUploadState({
                                    type:
                                        uploadState.files.length > 1
                                            ? "attached"
                                            : "none",
                                    files: uploadState.files.slice(0, -1),
                                });
                            }
                        }

                        debouncedStopTyping(true);
                    }}
                    placeholder={
                        channel.channel_type === "DirectMessage"
                            ? translate("app.main.channel.message_who", {
                                  person: getDisplayName(channel.recipient),
                              })
                            : channel.channel_type === "SavedMessages"
                              ? translate("app.main.channel.message_saved")
                              : translate("app.main.channel.message_where", {
                                    channel_name: channel.name ?? undefined,
                                })
                    }
                    disabled={
                        uploadState.type === "uploading" ||
                        uploadState.type === "sending"
                    }
                    onClick={(e) => setCursorPoint(e.target.selectionStart)}
                    onChange={(e) => {
                        setMessage(e.currentTarget.value);
                        startTyping();
                        onChange(e);
                    }}
                    onFocus={onFocus}
                    onBlur={onBlur}
                />

                <RightContainer>
                    <ActionsGroup>
                        <Action>
                            <IconButton onClick={() => setPicker(!picker)}>
                                <HappyBeaming size={24} />
                            </IconButton>
                        </Action>
                        <Action>
                            <IconButton
                                className={
                                    state.settings.get(
                                        "appearance:show_send_button",
                                    )
                                        ? ""
                                        : "mobile"
                                }
                                onClick={send}
                                onMouseDown={(e) => e.preventDefault()}>
                                <Send size={20} />
                            </IconButton>
                        </Action>
                    </ActionsGroup>

                    <CharsCount>
                        {currentMessageContentLength >
                            MAX_MESSAGE_LENGTH -
                                MAX_MESSAGE_LENGTH_OFFSET_BEFORE_WARNING &&
                        currentMessageContentLength < MAX_MESSAGE_LENGTH ? (
                            MAX_MESSAGE_LENGTH - currentMessageContentLength
                        ) : currentMessageContentLength > MAX_MESSAGE_LENGTH ? (
                            <ErrorComponent
                                error={`-${
                                    currentMessageContentLength -
                                    MAX_MESSAGE_LENGTH
                                }`}
                            />
                        ) : (
                            ""
                        )}
                    </CharsCount>
                </RightContainer>
            </Base>
        </>
    );
});
